<template>
  <Transition name="modal-fade">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 dark:bg-brand-900/60 backdrop-blur-xl"
      @click.self="closeModal"
    >
      <div class="w-full max-w-3xl rounded-[28px] border bg-white dark:bg-brand-800 border-slate-100 dark:border-brand-700/50 shadow-2xl p-8 sm:p-12">
        
        <div class="text-center mb-8">
          <img src="@/assets/1.png" alt="ControlEase" class="h-14 mx-auto mb-6"/>
          <h1 class="text-3xl font-bold tracking-tight text-slate-900 dark:text-white"><span class="bg-gradient-to-r from-brand-500 to-fuchsia-500 dark:from-purple-400 dark:to-violet-300 bg-clip-text text-transparent">Crie sua conta</span></h1>
          <p class="text-sm text-slate-600 dark:text-slate-400 mt-2">Passo 1 de 2</p>
        </div>

        <div v-if="feedbackMessage.text" :class="['mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium border',
          feedbackMessage.type === 'success'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-400/30'
            : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-400/30']">
          <span class="material-symbols-outlined text-xl">{{ feedbackMessage.type === 'success' ? 'check_circle' : 'error' }}</span>
          <span class="truncate">{{ feedbackMessage.text }}</span>
        </div>

        <form @submit.prevent="handleRegister" class="space-y-6" novalidate>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label for="reg-nome" class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Nome completo</label>
              <input id="reg-nome" v-model="form.nome" type="text" placeholder="Seu nome completo" required class="w-full rounded-2xl border bg-slate-50/70 dark:bg-brand-900/40 border-slate-200 dark:border-brand-700/50 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500"/>
            </div>
            <div>
              <label for="reg-username" class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Nome de usuário</label>
              <div class="relative">
                <input id="reg-username" v-model="form.username" type="text" placeholder="Seu apelido único" required 
                       class="w-full rounded-2xl border bg-slate-50/70 dark:bg-brand-900/40 border-slate-200 dark:border-brand-700/50 px-4 py-3 pr-10 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500" 
                       :class="{ '!border-rose-500 dark:!border-rose-500': form.username && !usernameAvailable, '!border-emerald-500 dark:!border-emerald-500': usernameAvailable && form.username }"/>
                <span v-if="isCheckingUsername" class="absolute right-3 top-1/2 -translate-y-1/2 loader-small"></span>
              </div>
              <p v-if="usernameFeedback" class="text-xs mt-1" :class="{ 'text-rose-600 dark:text-rose-400': !usernameAvailable, 'text-emerald-600 dark:text-emerald-400': usernameAvailable }">{{ usernameFeedback }}</p>
            </div>
          </div>

          <div>
            <label for="reg-email" class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">E‑mail</label>
            <input id="reg-email" v-model="form.email" type="email" placeholder="seu.email@exemplo.com" required class="w-full rounded-2xl border bg-slate-50/70 dark:bg-brand-900/40 border-slate-200 dark:border-brand-700/50 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500"/>
          </div>

          <div>
            <label for="reg-telefone" class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Telefone</label>
            <div class="relative">
              <input id="reg-telefone" v-model="form.telefone" type="tel" placeholder="(XX) XXXXX‑XXXX" required maxlength="15" @input="formatTelefone" 
                     class="w-full rounded-2xl border bg-slate-50/70 dark:bg-brand-900/40 border-slate-200 dark:border-brand-700/50 px-4 py-3 pr-10 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500" 
                     :class="{ '!border-rose-500 dark:!border-rose-500': form.telefone && !telefoneAvailable, '!border-emerald-500 dark:!border-emerald-500': telefoneAvailable && form.telefone.length >= 14 }"/>
              <span v-if="isCheckingTelefone" class="absolute right-3 top-1/2 -translate-y-1/2 loader-small"></span>
            </div>
            <p v-if="telefoneFeedback" class="text-xs mt-1" :class="{ 'text-rose-600 dark:text-rose-400': !telefoneAvailable, 'text-emerald-600 dark:text-emerald-400': telefoneAvailable }">{{ telefoneFeedback }}</p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label for="reg-password" class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Senha</label>
              <input id="reg-password" v-model="form.password" type="password" placeholder="Crie uma senha forte" required class="w-full rounded-2xl border bg-slate-50/70 dark:bg-brand-900/40 border-slate-200 dark:border-brand-700/50 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500"/>
            </div>
            <div>
              <label for="reg-passwordConfirm" class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Confirmar senha</label>
              <input id="reg-passwordConfirm" v-model="form.passwordConfirm" type="password" placeholder="Repita a senha" required class="w-full rounded-2xl border bg-slate-50/70 dark:bg-brand-900/40 border-slate-200 dark:border-brand-700/50 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500"/>
            </div>
          </div>

          <button type="submit" :disabled="isLoading || !usernameAvailable || !telefoneAvailable" class="group relative w-full rounded-2xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-indigo-600 px-5 py-3.5 text-sm font-semibold text-white shadow-[0_12px_40px_-8px_rgba(124,58,237,.55)] transition active:scale-[.99] disabled:opacity-70">
            <span v-if="isLoading" class="loader-modern mr-2 align-middle"></span>
            <span class="relative z-10">{{ isLoading ? 'Avançando…' : 'Avançar para Endereço' }}</span>
            <span class="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/10"></span>
          </button>

          <div class="mt-8 text-center text-sm text-slate-700 dark:text-slate-300">Já tem conta?
            <a href="#" @click.prevent="switchToLogin" class="font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300">Faça login</a>
          </div>
        </form>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import authService from '@/service/authService'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'switch-view', 'go-to-address'])

const closeModal = () => {
  emit('close')
}
const switchToLogin = () => {
  emit('switch-view', 'login')
}

const form = reactive({ nome: '', username: '', email: '', telefone: '', password: '', passwordConfirm: '' })
const isLoading = ref(false)
const feedbackMessage = ref({ text: '', type: '' })

const isCheckingUsername = ref(false)
const usernameAvailable = ref(true)
const usernameFeedback = ref('')
let usernameDebounceTimer = null

const isCheckingTelefone = ref(false)
const telefoneAvailable = ref(true)
const telefoneFeedback = ref('')
let telefoneDebounceTimer = null

const resetForm = () => {
  Object.assign(form, { nome: '', username: '', email: '', telefone: '', password: '', passwordConfirm: '' })
  feedbackMessage.value = { text: '', type: '' }
  isLoading.value = false
  usernameAvailable.value = true
  usernameFeedback.value = ''
  telefoneAvailable.value = true
  telefoneFeedback.value = ''
  clearTimeout(usernameDebounceTimer)
  clearTimeout(telefoneDebounceTimer)
}

watch(() => props.show, (newVal) => {
  if (newVal) {
    resetForm()
  }
})

watch(() => form.username, (val) => {
  clearTimeout(usernameDebounceTimer)
  usernameFeedback.value = ''
  if (!val.trim()) { usernameAvailable.value = false; return }
  isCheckingUsername.value = true
  usernameFeedback.value = 'Verificando…'
  usernameDebounceTimer = setTimeout(async () => {
    try {
      const r = await authService.checkUsername(val)
      usernameAvailable.value = r.available
      usernameFeedback.value = r.available ? 'Nome de usuário disponível!' : 'Este nome de usuário já está em uso.'
    } catch (e) { usernameFeedback.value = 'Erro ao verificar' }
    isCheckingUsername.value = false
  }, 500)
})

watch(() => form.telefone, (tel) => {
  clearTimeout(telefoneDebounceTimer)
  telefoneFeedback.value = ''
  const clean = tel.replace(/\D/g, '')
  if (!(clean.length === 10 || clean.length === 11)) { telefoneAvailable.value = false; return }
  isCheckingTelefone.value = true
  telefoneFeedback.value = 'Verificando…'
  telefoneDebounceTimer = setTimeout(async () => {
    try {
      const r = await authService.checkTelefone(clean)
      telefoneAvailable.value = r.available
      telefoneFeedback.value = r.available ? 'Telefone disponível!' : 'Este nome de usuário já está em uso.'
    } catch (e) { telefoneFeedback.value = 'Erro ao verificar' }
    isCheckingTelefone.value = false
  }, 500)
})

const formatTelefone = (e) => {
  let v = e.target.value.replace(/\D/g, '')
  if (v.length > 11) v = v.slice(0, 11)
  if (v.length > 6) v = `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`
  else if (v.length > 2) v = `(${v.slice(0,2)}) ${v.slice(2)}`
  else if (v.length > 0) v = `(${v}`
  form.telefone = v
}

const handleRegister = async () => {
  if (!usernameAvailable.value || !telefoneAvailable.value) { feedbackMessage.value = { text: 'Verifique os campos em vermelho.', type: 'error' }; return }
  if (form.password !== form.passwordConfirm) { feedbackMessage.value = { text: 'As senhas não coincidem.', type: 'error' }; return }
  isLoading.value = true
  feedbackMessage.value = { text: '', type: '' }
  const payload = { ...form }
  delete payload.passwordConfirm
  let clean = payload.telefone.replace(/\D/g, '')
  if (clean.length === 11 && clean.charAt(2) === '9') clean = clean.substring(0,2) + clean.substring(3)
  payload.telefone = `+55${clean}`
  try {
    const r = await authService.register(payload)
    if (r.success) { 
      feedbackMessage.value = { text: 'Usuário criado! Indo para o endereço…', type: 'success' }
      setTimeout(() => {
        emit('go-to-address', { userId: r.data.id })
        resetForm()
      }, 1200) 
    }
    else { feedbackMessage.value = { text: r.message || 'Não foi possível criar a conta.', type: 'error' } }
  } catch (e) {
    feedbackMessage.value = { text: 'Ocorreu um erro no servidor.', type: 'error' }
  } finally { isLoading.value = false }
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.loader-modern{width:18px;height:18px;border:2px solid transparent;border-top-color:#fff;border-right-color:#fff;border-radius:50%;display:inline-block;animation:spin .7s linear infinite}
.loader-small{width:16px;height:16px;border:2px solid #9ca3af;border-top-color:transparent;border-radius:50%;display:inline-block;animation:spin .8s linear infinite}
.dark .loader-small { border-color: #475569; border-top-color: transparent; }
@keyframes spin{to{transform:rotate(360deg)}}
</style>